import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  HelpCircle, 
  MessageCircle, 
  Phone, 
  Mail, 
  Search, 
  ExternalLink,
  Clock,
  Users,
  Download,
  Play
} from 'lucide-react';
import { contactInfo } from '../config/contactInfo';

const Support: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredFAQs, setFilteredFAQs] = useState<any[]>([]);

  // TODO: Implement Live Chat functionality
  // TODO: Add comprehensive documentation section

  const supportOptions = [
    {
      icon: Phone,
      title: 'Phone Support',
      description: 'Speak directly with our experts',
      availability: contactInfo.support.phone.availability,
      action: 'Call Now',
      color: 'text-secondary-400',
      href: `tel:${contactInfo.phone.primary}`,
      contactMethod: 'phone'
    },
    {
      icon: Mail,
      title: 'Email Support',
      description: 'Send detailed questions and get comprehensive answers',
      availability: contactInfo.support.email.availability,
      action: 'Send Email',
      color: 'text-accent-400',
      href: `mailto:${contactInfo.email.support}`,
      contactMethod: 'email'
    }
  ];

  const faqItems = [
    {
      question: 'How do I get started with BioAxis?',
      answer: 'Getting started is simple! Sign up for a free trial, and our onboarding team will guide you through the setup process. We provide comprehensive training and support to ensure smooth implementation.',
      keywords: ['getting started', 'trial', 'onboarding', 'setup', 'implementation']
    },
    {
      question: 'What is the difference between BioAxis and other EHR systems?',
      answer: 'BioAxis is a cloud-based EHR system that is designed to be modular and scalable. It is built to be integrated with other systems and to be used in a variety of settings. It is not a traditional EHR system that is tied to a specific practice or hospital.',
      keywords: ['difference', 'EHR', 'systems', 'integration', 'modularity', 'scalability']
    },
    {
      question: 'Is my patient data secure with BioAxis?',
      answer: 'Yes! We use industry-leading encryption both in transit and at rest. Our security measures include multi-factor authentication, regular audits, and 24/7 monitoring. We are working towards full compliance certifications.',
      keywords: ['security', 'data', 'patient', 'encryption', 'monitoring', 'compliance']
    },
    {
      question: 'Can I integrate BioAxis with my existing systems?',
      answer: 'Yes! BioAxis integrates with most major EHR systems, practice management software, and lab systems. For complex integrations, we can setup a call to implement custom integration solutions.',
      keywords: ['integration', 'EHR', 'systems', 'API', 'custom', 'setup call'],
      hasCallToAction: true,
      actionText: 'Schedule Integration Call',
      actionHref: `mailto:${contactInfo.email.support}?subject=Integration%20Setup%20Call&body=Hello,%0D%0A%0D%0AI%20would%20like%20to%20schedule%20a%20call%20to%20discuss%20integrating%20BioAxis%20with%20my%20existing%20systems.%0D%0A%0D%0APlease%20let%20me%20know%20your%20availability.%0D%0A%0D%0AThank%20you!`
    },
    {
      question: 'What kind of training do you provide?',
      answer: 'We offer comprehensive training including live webinars, one-on-one sessions, video tutorials, and written documentation. Training is included in all our plans.',
      keywords: ['training', 'webinars', 'tutorials', 'documentation', 'support']
    },
    {
      question: 'How much does BioAxis cost?',
      answer: 'Our pricing is based on the number of users and features needed. We offer flexible monthly and annual plans. Contact our sales team for a custom quote tailored to your practice.',
      keywords: ['pricing', 'cost', 'plans', 'quote', 'monthly', 'annual']
    }
  ];

  // Search functionality
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim() === '') {
      setFilteredFAQs([]);
    } else {
      const filtered = faqItems.filter(faq => 
        faq.question.toLowerCase().includes(query.toLowerCase()) ||
        faq.answer.toLowerCase().includes(query.toLowerCase()) ||
        faq.keywords.some(keyword => keyword.toLowerCase().includes(query.toLowerCase()))
      );
      setFilteredFAQs(filtered);
    }
  };

  // Navigate to FAQ section and highlight specific item
  const navigateToFAQ = (faqIndex: number) => {
    // Clear search
    setSearchQuery('');
    setFilteredFAQs([]);
    
    // Scroll to FAQ section first
    const faqSection = document.getElementById('faq-section');
    if (faqSection) {
      faqSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // After scrolling, highlight the specific FAQ item
      setTimeout(() => {
        const faqItem = document.getElementById(`faq-item-${faqIndex}`);
        if (faqItem) {
          // Add highlight effect
          faqItem.style.backgroundColor = 'rgba(26, 36, 54, 0.2)';
          faqItem.style.border = '2px solid rgba(26, 36, 54, 0.5)';
          
          // Remove highlight after 3 seconds
          setTimeout(() => {
            faqItem.style.backgroundColor = '';
            faqItem.style.border = '';
          }, 3000);
        }
      }, 800);
    }
  };

  // TODO: Implement Additional Resources section
  // TODO: Add Video Tutorials with actual video content
  // TODO: Add Downloads section with real user manuals and guides
  // TODO: Create Community Forum for user discussions
  
  const resources = [
    {
      icon: Play,
      title: 'Video Tutorials',
      description: 'Step-by-step video guides for all features',
      items: ['Getting Started', 'Advanced Features', 'Best Practices'],
      link: '#'
    },
    {
      icon: Download,
      title: 'Downloads',
      description: 'User manuals, quick reference guides, and more',
      items: ['User Manual PDF', 'Quick Start Guide', 'Feature Checklist'],
      link: '#'
    },
    {
      icon: Users,
      title: 'Community Forum',
      description: 'Connect with other BioAxis users',
      items: ['Discussion Groups', 'Feature Requests', 'Tips & Tricks'],
      link: '#'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-slate-100/50 dark:bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <HelpCircle className="w-16 h-16 text-primary-400 mx-auto mb-6" />
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-slate-900 dark:text-white">
              Help & <span className="gradient-text">Support</span>
            </h1>
            <p className="text-xl text-slate-700 dark:text-gray-300 max-w-3xl mx-auto mb-8">
              We're here to help you succeed with BioAxis. Find answers, get support, and learn how to make the most of our platform.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-md mx-auto relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search for help..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
              />
              {searchQuery && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-900 border border-slate-300 dark:border-white/10 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                  {filteredFAQs.length > 0 ? (
                    filteredFAQs.map((faq, searchIndex) => {
                      // Find the original index of this FAQ in the full list
                      const originalIndex = faqItems.findIndex(item => item.question === faq.question);
                      return (
                        <div 
                          key={searchIndex} 
                          className="p-3 hover:bg-slate-100 dark:hover:bg-white/5 cursor-pointer border-b border-slate-200 dark:border-white/5 last:border-b-0 transition-colors"
                          onClick={() => navigateToFAQ(originalIndex)}
                        >
                          <h4 className="font-semibold text-sm text-slate-900 dark:text-white mb-1">{faq.question}</h4>
                          <p className="text-xs text-slate-600 dark:text-gray-400 line-clamp-2">{faq.answer}</p>
                        </div>
                      );
                    })
                  ) : (
                    <div className="p-3 text-slate-600 dark:text-gray-400 text-sm">No results found</div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Support Options */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold mb-4 text-slate-900 dark:text-white">How Can We Help You?</h2>
            <p className="text-slate-700 dark:text-gray-300 max-w-2xl mx-auto">
              Choose the support option that works best for you. Our team is available to help with any questions or issues.
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-8 max-w-4xl mx-auto">
            {supportOptions.map((option, index) => (
                              <motion.div
                key={option.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-effect glass-effect-hover p-6 rounded-xl text-center group w-80"
              >
                <option.icon className={`w-12 h-12 ${option.color} mx-auto mb-4 group-hover:scale-110 transition-transform`} />
                <h3 className="text-xl font-bold mb-2 text-slate-900 dark:text-white">{option.title}</h3>
                <p className="text-slate-700 dark:text-gray-300 text-sm mb-3">{option.description}</p>
                <div className="flex items-center justify-center gap-1 text-xs text-slate-600 dark:text-gray-400 mb-4">
                  <Clock className="w-3 h-3" />
                  {option.availability}
                </div>
                <motion.a
                  href={option.href}
                  whileHover={{ scale: 1.02 }}
                  className={`w-full text-sm inline-block text-center px-6 py-3 rounded-xl font-semibold transition-colors ${
                    option.contactMethod === 'phone'
                      ? 'bg-secondary-500 hover:bg-secondary-600 text-white'
                      : 'btn-primary'
                  }`}
                >
                  {option.action}
                </motion.a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq-section" className="py-20 bg-slate-100/50 dark:bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold mb-4 text-slate-900 dark:text-white">Frequently Asked Questions</h2>
            <p className="text-slate-700 dark:text-gray-300">
              Find quick answers to the most common questions about BioAxis.
            </p>
          </motion.div>

          <div className="space-y-6">
            {faqItems.map((faq, index) => (
              <motion.div
                key={index}
                id={`faq-item-${index}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-effect glass-effect-hover p-6 rounded-xl transition-all duration-300"
              >
                <h3 className="text-lg font-bold mb-3 flex items-start gap-3 text-slate-900 dark:text-white">
                  <HelpCircle className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                  {faq.question}
                </h3>
                <p className="text-slate-700 dark:text-gray-300 pl-8 mb-4">{faq.answer}</p>
                {faq.hasCallToAction && (
                  <div className="pl-8">
                    <motion.a
                      href={faq.actionHref}
                      whileHover={{ scale: 1.02 }}
                      className="btn-primary inline-block text-sm"
                    >
                      {faq.actionText}
                    </motion.a>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TODO: Resources Section - Hidden for now */}
      {false && (
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl font-bold mb-4">Additional Resources</h2>
              <p className="text-gray-300">
                Explore our library of resources to help you master BioAxis.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {resources.map((resource, index) => (
                <motion.div
                  key={resource.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="glass-effect glass-effect-hover p-6 rounded-xl"
                >
                  <resource.icon className="w-8 h-8 text-primary-400 mb-4" />
                  <h3 className="text-xl font-bold mb-2">{resource.title}</h3>
                  <p className="text-gray-300 text-sm mb-4">{resource.description}</p>
                  <ul className="space-y-2 mb-6">
                    {resource.items.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                        <div className="w-1.5 h-1.5 bg-primary-400 rounded-full" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <motion.a
                    href={resource.link}
                    whileHover={{ scale: 1.02 }}
                    className="btn-outline w-full flex items-center justify-center gap-2 text-sm"
                  >
                    Explore
                    <ExternalLink className="w-4 h-4" />
                  </motion.a>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Emergency Support */}
      <section className="py-20 bg-slate-100/50 dark:bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl"
          >
            <Phone className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h3 className="text-2xl font-bold mb-4 text-slate-900 dark:text-white">Emergency Support</h3>
            <p className="text-slate-700 dark:text-gray-300 mb-6">
              Experiencing a critical issue that affects patient care? Our emergency support team is available {contactInfo.support.emergency.availability}.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href={`tel:${contactInfo.phone.emergency}`}
                whileHover={{ scale: 1.05 }}
                className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Emergency Hotline
              </motion.a>
              <motion.a
                href={`mailto:${contactInfo.email.emergency}`}
                whileHover={{ scale: 1.05 }}
                className="bg-white dark:bg-gray-800 text-slate-900 dark:text-white border border-slate-300 dark:border-gray-600 hover:bg-slate-100 dark:hover:bg-gray-700 px-6 py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
              >
                Emergency Email
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Support; 
